// src/hooks/useProjectMemory.ts
import { useState, useCallback, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ProjectMemory {
  id?: string;
  project_id: string;
  app_purpose?: string;
  features: Array<{ name: string; description: string; addedAt: string }>;
  architecture_decisions: Array<{ decision: string; reason: string; madeAt: string }>;
  prompt_history: Array<{ prompt: string; summary: string; timestamp: string }>;
  tech_stack: {
    framework?: string;
    ui?: string;
    backend?: string;
    database?: string;
    [key: string]: string | undefined;
  };
  custom_context?: string;
}

const DEFAULT_MEMORY: Omit<ProjectMemory, "project_id"> = {
  features: [],
  architecture_decisions: [],
  prompt_history: [],
  tech_stack: {},
};

function isUuid(v: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    v
  );
}

function safeArray<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

function safeObject(v: unknown): Record<string, any> {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as any) : {};
}

export function useProjectMemory(projectId: string | null) {
  const [memory, setMemory] = useState<ProjectMemory | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // ✅ dev projects use ids like "dev-..." which are not UUIDs.
  const isDevProject = useMemo(
    () => !!projectId && projectId.startsWith("dev-"),
    [projectId]
  );

  // ✅ only hit DB when projectId is a real UUID (prevents PostgREST 400s)
  const canUseDb = useMemo(
    () => !!projectId && !isDevProject && isUuid(projectId),
    [projectId, isDevProject]
  );

  const loadMemory = useCallback(async (projId: string) => {
    // Local-only when not a UUID (dev or otherwise)
    if (projId.startsWith("dev-") || !isUuid(projId)) {
      setIsLoading(false);
      setMemory({ project_id: projId, ...DEFAULT_MEMORY });
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase
        .from("project_memory")
        .select("*")
        .eq("project_id", projId)
        .maybeSingle();

      // PGRST116 = "0 rows" for maybeSingle()
      if (error && (error as any).code !== "PGRST116") {
        console.error("Failed to load project memory:", error);
        return;
      }

      if (!data) {
        setMemory({ project_id: projId, ...DEFAULT_MEMORY });
        return;
      }

      const row: any = data;

      setMemory({
        id: row.id,
        project_id: row.project_id,
        app_purpose: row.app_purpose ?? undefined,
        custom_context: row.custom_context ?? undefined,
        features: safeArray(row.features),
        architecture_decisions: safeArray(row.architecture_decisions),
        prompt_history: safeArray(row.prompt_history),
        tech_stack: safeObject(row.tech_stack),
      });
    } catch (err) {
      console.error("Error loading memory:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!projectId) {
        if (!cancelled) setMemory(null);
        return;
      }
      if (!cancelled) await loadMemory(projectId);
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, loadMemory]);

  const saveMemory = useCallback(
    async (updates: Partial<ProjectMemory>) => {
      if (!projectId) return false;

      const base: ProjectMemory =
        memory ?? ({
          project_id: projectId,
          ...DEFAULT_MEMORY,
        } as ProjectMemory);

      const updated: ProjectMemory = { ...base, ...updates, project_id: projectId };
      setMemory(updated);

      // Local-only when we can't use DB
      if (!canUseDb) return true;

      try {
        const { error } = await supabase
          .from("project_memory")
          .upsert(
            {
              project_id: projectId,
              app_purpose: updated.app_purpose ?? null,
              features: (updated.features ?? []) as any,
              architecture_decisions: (updated.architecture_decisions ?? []) as any,
              prompt_history: (updated.prompt_history ?? []) as any,
              tech_stack: (updated.tech_stack ?? {}) as any,
              custom_context: updated.custom_context ?? null,
              // ✅ your table has updated_at
              updated_at: new Date().toISOString(),
            } as any,
            { onConflict: "project_id" }
          );

        if (error) {
          console.error("Failed to save project memory:", error);
          return false;
        }
        return true;
      } catch (err) {
        console.error("Error saving memory:", err);
        return false;
      }
    },
    [projectId, memory, canUseDb]
  );

  const addPromptToHistory = useCallback(
    async (prompt: string, summary: string) => {
      if (!projectId) return;

      const base =
        memory ??
        ({
          project_id: projectId,
          ...DEFAULT_MEMORY,
        } as ProjectMemory);

      const newEntry = { prompt, summary, timestamp: new Date().toISOString() };
      const updatedHistory = [...(base.prompt_history ?? []), newEntry].slice(-20);

      await saveMemory({ prompt_history: updatedHistory });
    },
    [memory, projectId, saveMemory]
  );

  const addFeature = useCallback(
    async (name: string, description: string) => {
      if (!projectId) return;

      const base =
        memory ??
        ({
          project_id: projectId,
          ...DEFAULT_MEMORY,
        } as ProjectMemory);

      const newFeature = { name, description, addedAt: new Date().toISOString() };
      await saveMemory({ features: [...(base.features ?? []), newFeature] });
    },
    [memory, projectId, saveMemory]
  );

  const addArchitectureDecision = useCallback(
    async (decision: string, reason: string) => {
      if (!projectId) return;

      const base =
        memory ??
        ({
          project_id: projectId,
          ...DEFAULT_MEMORY,
        } as ProjectMemory);

      const newDecision = { decision, reason, madeAt: new Date().toISOString() };
      await saveMemory({
        architecture_decisions: [...(base.architecture_decisions ?? []), newDecision],
      });
    },
    [memory, projectId, saveMemory]
  );

  const setAppPurpose = useCallback(
    async (purpose: string) => {
      await saveMemory({ app_purpose: purpose });
    },
    [saveMemory]
  );

  const updateTechStack = useCallback(
    async (updates: ProjectMemory["tech_stack"]) => {
      if (!projectId) return;

      const base =
        memory ??
        ({
          project_id: projectId,
          ...DEFAULT_MEMORY,
        } as ProjectMemory);

      await saveMemory({ tech_stack: { ...(base.tech_stack ?? {}), ...updates } });
    },
    [memory, projectId, saveMemory]
  );

  const buildContextString = useCallback((): string => {
    if (!memory) return "";

    const lines: string[] = [];

    if (memory.app_purpose) lines.push(`## App Purpose\n${memory.app_purpose}\n`);

    if (memory.tech_stack && Object.keys(memory.tech_stack).length > 0) {
      lines.push("## Tech Stack");
      for (const [key, value] of Object.entries(memory.tech_stack)) {
        if (value) lines.push(`- ${key}: ${value}`);
      }
      lines.push("");
    }

    if (memory.features.length > 0) {
      lines.push("## Existing Features");
      for (const feature of memory.features.slice(-10)) {
        lines.push(`- **${feature.name}**: ${feature.description}`);
      }
      lines.push("");
    }

    if (memory.architecture_decisions.length > 0) {
      lines.push("## Architecture Decisions");
      for (const d of memory.architecture_decisions.slice(-5)) {
        lines.push(`- ${d.decision} (Reason: ${d.reason})`);
      }
      lines.push("");
    }

    if (memory.prompt_history.length > 0) {
      lines.push("## Recent Changes (Last 5 prompts)");
      for (const entry of memory.prompt_history.slice(-5)) {
        lines.push(`- "${entry.prompt.slice(0, 100)}..." → ${entry.summary}`);
      }
      lines.push("");
    }

    if (memory.custom_context) lines.push(`## Additional Context\n${memory.custom_context}\n`);

    return lines.join("\n");
  }, [memory]);

  return {
    memory,
    isLoading,
    loadMemory,
    saveMemory,
    addPromptToHistory,
    addFeature,
    addArchitectureDecision,
    setAppPurpose,
    updateTechStack,
    buildContextString,
    isDevProject,
    canUseDb,
  };
}
